import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader } from 'lucide-react';
import Header from '../components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [booking, setBooking] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        let attempts = 0;
        const maxAttempts = 5;

        const poll = async () => {
          if (attempts >= maxAttempts) {
            setStatus('timeout');
            return;
          }

          try {
            const response = await axios.get(
              `${API}/payments/checkout/status/${sessionId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.payment_status === 'paid') {
              setStatus('success');
              return;
            }

            attempts++;
            setTimeout(poll, 2000);
          } catch (error) {
            console.error('Failed to check payment status:', error);
            setStatus('error');
          }
        };

        poll();
      } catch (error) {
        console.error('Payment verification failed:', error);
        setStatus('error');
      }
    };

    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId, user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={() => {}} />
      
      <div className="flex items-center justify-center px-6 py-24">
        <div className="max-w-md w-full bg-surface rounded-2xl p-8 border border-border text-center" data-testid="payment-success-page">
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h2 className="font-heading text-2xl font-bold text-text-primary mb-3">Processing Payment</h2>
              <p className="text-text-secondary">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-success mx-auto mb-6" />
              <h2 className="font-heading text-2xl font-bold text-text-primary mb-3">Payment Successful!</h2>
              <p className="text-text-secondary mb-8">Your booking has been confirmed. Check your dashboard for details.</p>
              <button
                onClick={() => navigate('/dashboard/customer')}
                className="btn-primary w-full"
                data-testid="go-to-dashboard-button"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">⚠️</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-text-primary mb-3">Payment Error</h2>
              <p className="text-text-secondary mb-8">There was an issue processing your payment. Please try again.</p>
              <button onClick={() => navigate('/')} className="btn-primary w-full">
                Back to Home
              </button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">⏱️</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-text-primary mb-3">Verification Timeout</h2>
              <p className="text-text-secondary mb-8">Payment verification is taking longer than expected. Check your email for confirmation.</p>
              <button onClick={() => navigate('/dashboard/customer')} className="btn-primary w-full">
                Go to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
